import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismAccordion,
  PrismBadge,
  PrismTable,
} from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import { gt } from 'ember-truth-helpers';
import { OVERRIDE_TYPES } from 'lumen/utils/constants/pricing-assistant-constants';
import { getOverrideVariant } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

import TdAutomationCheckbox from './cells/td-automation-checkbox';
import TdMinLosRecommendation from './cells/td-min-los-recommendation';
import TdRateGroupName from './cells/td-rate-group-name';
import type { StayRestrictionsAccordionState } from './stay-restrictions-accordion';

/* The slice of a MinLOS state the row's cells read; MinLosRestrictionState
   satisfies it structurally. */
export interface RateGroupRowState extends Pick<
  StayRestrictionsAccordionState,
  | 'automationOn'
  | 'formattedRecommendation'
  | 'isLoading'
  | 'onBlur'
  | 'onTypeChange'
  | 'restrictionType'
  | 'toggleAutomation'
  | 'touched'
  | 'updateValue'
  | 'value'
> {
  formattedCurrentValue: string;
}

export interface RateGroupRow extends CustomEmberTableRow {
  name: string;
  /* Namespaces the row's radio group name and the element ids its controls are
     labelled by. */
  rateGroupId: string;
  /* The row's MinLOS state; the cells read their values and handlers from it. */
  state: RateGroupRowState;
}

interface RateGroupsAccordionSignature {
  Args: {
    overrideCount: number;
    rows: RateGroupRow[];
    showEmptyState?: boolean;
  };
  Blocks: {
    emptyState: [];
    settingsLink: [];
  };
}

export default class RateGroupsAccordion extends Component<RateGroupsAccordionSignature> {
  @service declare intl: Services['intl'];

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        isSortable: false,
        textAlign: 'left',
        name: this.intl.t('pricing_assistant.stay_restrictions.rate_group'),
        tdComponent: TdRateGroupName,
        valuePath: 'name',
        width: 120,
      },
      {
        isSortable: false,
        textAlign: 'left',
        name: this.intl.t('pricing_manager.automation_on'),
        tdComponent: TdAutomationCheckbox,
        valuePath: 'state.automationOn',
        width: 103,
      },
      {
        isSortable: false,
        textAlign: 'left',
        name: this.intl.t('pricing_manager.restrictions.min_los_label'),
        tdClassNames: 'p-200',
        tdComponent: TdMinLosRecommendation,
        valuePath: 'state.formattedRecommendation',
        minWidth: 212,
        width: 212,
      },
      {
        isSortable: false,
        textAlign: 'left',
        name: this.intl.t(
          'pricing_assistant.stay_restrictions.min_los_currently_in_pms',
        ),
        valuePath: 'state.formattedCurrentValue',
        width: 81,
      },
    ];
  }

  <template>
    <PrismAccordion
      @initiallyOpened={{true}}
      @title={{t "pricing_assistant.stay_restrictions.rate_groups"}}
      class="mx-500 my-400"
      data-test-id="rate-groups-accordion"
    >
      <:summaryAfter>
        {{#if (gt @overrideCount 0)}}
          <PrismBadge
            @iconName={{getOverrideVariant OVERRIDE_TYPES.MIN_LOS}}
            @label={{t
              "pricing_assistant.stay_restrictions.override_count"
              count=@overrideCount
            }}
            @type="warning"
            data-test-id="rate-groups-override-badge"
          />
        {{/if}}
      </:summaryAfter>
      <:default>
        {{#if @showEmptyState}}
          {{yield to="emptyState"}}
        {{else}}
          <PrismTable
            @columns={{this.columns}}
            @fillColumnIndex={{2}}
            @fillMode="nth-column"
            @highlightTitleColumn={{false}}
            @rows={{@rows}}
            data-test-id="rate-groups-table"
          />
          {{#if (has-block "settingsLink")}}
            <div class="mt-400">
              {{yield to="settingsLink"}}
            </div>
          {{/if}}
        {{/if}}
      </:default>
    </PrismAccordion>
  </template>
}
